<template>
  <div class="bcmp-scroll">
    <div :style="canvasStyle">
      <BarChart
        :chartData="chartData"
        :chartOptions="chartOptions"
        :styles="chartStyles"
        :chart-id="chartId"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Chart as ChartJS, LineController, LineElement, PointElement } from 'chart.js'
import BarChart from '@/components/atom/chart/barChart'
import { LINE_MARK, alignedPeriods, axisScaleOf, formatAmount, formatAxisValue } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

ChartJS.register(LineController, LineElement, PointElement)

const ACTUAL_MEASURE = 'actual'
const MAX_BARS = 36
const CHART_HEIGHT = 300
const WIDE_PERIODS = 6
const WIDE_CANVAS = 560
const YEAR_IN_LABEL = /\d{4}/
const TEXT_COLOR = '#667085'
const GRID_COLOR = '#f0f1f4'
const BASELINE_COLOR = '#d0d5dd'
const POINT_RING_COLOR = '#ffffff'
const TOOLTIP_COLOR = '#101828'

const props = defineProps({
  series: { type: Array, required: true },
  measure: { type: String, required: true },
  chartId: { type: String, required: true }
})

const isActual = props.measure === ACTUAL_MEASURE
const periods = alignedPeriods(props.series.map(item => item.rows))
const labels = periods.map(period => period.label)
const isLine = props.series.length * labels.length > MAX_BARS

const valueAt = (seriesIndex, position) => {
  const row = periods[position].rows[seriesIndex]
  if (! row) return null
  return row[props.measure]
}

const barMark = (color) => ({
  backgroundColor: color,
  borderWidth: 0,
  borderRadius: 4,
  barPercentage: 0.84,
  categoryPercentage: 0.74,
  maxBarThickness: 24
})

const lineMark = (color) => ({
  type: LINE_MARK,
  borderColor: color,
  backgroundColor: color,
  borderWidth: 2,
  borderDash: isActual ? [] : [6, 4],
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBorderColor: POINT_RING_COLOR,
  pointBorderWidth: 2,
  spanGaps: false
})

const chartData = computed(() => ({
  labels: labels,
  datasets: props.series.map((item, seriesIndex) => ({
    label: item.name,
    data: labels.map((label, position) => valueAt(seriesIndex, position)),
    ...(isLine ? lineMark(item.color) : barMark(item.color))
  }))
}))

const axisScale = computed(() => {
  const values = props.series.flatMap(item => item.rows.map(row => Math.abs(row[props.measure] || 0)))
  return axisScaleOf(Math.max(0, ...values))
})

const canvasStyle = computed(() => ({
  position: 'relative',
  minWidth: labels.length > WIDE_PERIODS ? `${WIDE_CANVAS}px` : '0'
}))

const chartStyles = computed(() => ({
  position: 'relative',
  height: `${CHART_HEIGHT}px`
}))

const amountText = (row) => {
  const value = row[props.measure]
  return value === null ? 'Not started' : formatAmount(value)
}

const seriesLines = (item, seriesIndex, position) => {
  const row = periods[position].rows[seriesIndex]
  if (! row) return []
  return [item.name, `  ${row.label} · ${item.interval_name}: ${amountText(row)}`]
}

const categoryLabel = (value) => {
  const label = labels[value]
  if (! label) return ''
  return YEAR_IN_LABEL.test(label) ? label.split(' - ') : label
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', axis: 'x', intersect: false },
  scales: {
    x: {
      grid: { display: false, borderColor: BASELINE_COLOR },
      ticks: { autoSkip: false, maxRotation: 0, font: { size: 11 }, color: TEXT_COLOR, callback: categoryLabel }
    },
    y: {
      beginAtZero: true,
      grid: { color: GRID_COLOR, drawBorder: false, drawTicks: false },
      ticks: { padding: 8, font: { size: 11 }, color: TEXT_COLOR, callback: (value) => formatAxisValue(value, axisScale.value) }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: TOOLTIP_COLOR,
      cornerRadius: 8,
      padding: 12,
      bodySpacing: 4,
      titleMarginBottom: 8,
      displayColors: false,
      filter: (item, index) => index === 0,
      callbacks: {
        title: (items) => (items.length ? labels[items[0].dataIndex] : ''),
        label: () => '',
        afterBody: (items) => (items.length ? props.series.flatMap((item, seriesIndex) => seriesLines(item, seriesIndex, items[0].dataIndex)) : [])
      }
    }
  }
}
</script>

<style scoped>
.bcmp-scroll {
  overflow-x: auto;
}
</style>
